import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FeatureSlider } from "./FeatureSlider";
import "../styles/auth.scss";
import "../styles/google.scss";

const STEPS = ["database", "catalog", "admin", "open"];

/** Map of the API's progress (status.step) onto the four visible steps. */
const stepIndex = (state, step) => {
  if (state === "ready") return 4;
  if (step === "warming" || step === "store") return 3;
  if (step === "account") return 2;
  if (step === "active") return 1;
  return 0;
};

/**
 * Full-screen "your store is being built" page for the Google signup, in the
 * same split layout as the store-details form: the feature slider in the
 * brand-blue hero, progress on the right. It never pretends to be finished:
 * progress creeps toward 92% with time, and only the API's "ready" (job done,
 * store answering) completes it. Brand colours only, no 3D emoji.
 */
export const StoreBuildingLoader = ({ storeName, state, step, failed, onRetry }) => {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const clock = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(clock);
  }, []);

  const done = state === "ready";
  const current = Math.max(stepIndex(state, step), Math.min(2, Math.floor(elapsed / 12)));
  const progress = done ? 100 : Math.min(92, 8 + current * 18 + elapsed * 0.9);

  return (
    <div className={`sp-auth sp-auth--hero sp-build${done ? " is-done" : ""}${failed ? " is-failed" : ""}`}>
      <div className="sp-auth-brand">
        <div className="sp-auth-brand-top">
          <span className="sp-auth-logo">
            <img src="/logo-text-white.png" alt="StorkPlus" />
          </span>
        </div>
        <div className="sp-auth-brand-body">
          <span className="sp-auth-eyebrow">{t("google.whileBuilding", "While we build your store")}</span>
          <h1>{t("google.heroTitle", "Everything you need to sell, in one place")}</h1>
          <p>{t("google.heroText", "Your store comes with all of this switched on. Take a look while it gets ready.")}</p>
          <FeatureSlider />
        </div>
      </div>

      <div className="sp-auth-panel">
        <div className="sp-auth-card sp-build-card" role="status" aria-live="polite">
          <div className="sp-build-mark" aria-hidden="true">
            {failed ? (
              <span className="sp-build-mark-fail">!</span>
            ) : done ? (
              <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            ) : (
              <span className="sp-build-spinner" />
            )}
          </div>

          {failed ? (
            <>
              <h2>{t("google.failedTitle", "Something went wrong")}</h2>
              <p className="sp-auth-sub">{failed}</p>
              {onRetry ? (
                <button type="button" className="sp-build-retry" onClick={onRetry}>
                  {t("google.retry", "Try again")}
                </button>
              ) : null}
            </>
          ) : (
            <>
              <h2>
                {done
                  ? t("google.readyTitle", "Your store is ready!")
                  : t("google.buildingTitle", "Building {{store}}…", { store: storeName || t("google.yourStore", "your store") })}
              </h2>
              <p className="sp-auth-sub">
                {done
                  ? t("google.readySub", "Opening your dashboard…")
                  : t("google.buildingSub", "This takes about a minute. Keep this page open — we'll sign you in automatically.")}
              </p>

              <div className="sp-build-bar" aria-hidden="true">
                <span style={{ width: `${progress}%` }} />
              </div>
              <div className="sp-build-pct" aria-hidden="true">{Math.round(progress)}%</div>

              <ol className="sp-build-steps">
                {STEPS.map((key, i) => (
                  <li key={key} className={i < current || done ? "is-done" : i === current ? "is-active" : ""}>
                    <span className="sp-build-dot">
                      {i < current || done ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                      ) : (
                        i + 1
                      )}
                    </span>
                    {t(`google.steps.${key}`)}
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreBuildingLoader;
